import { useQueryClient } from "@tanstack/react-query";
import { Logo, useDocumentTitle } from "@unizpay/ui";
import { type ReactNode, useCallback, useState } from "react";
import { rememberProfile } from "../../lib/profile";
import { ME_KEY } from "../../lib/queries";
import type { Me, Profile } from "../../lib/types";
import { StepNav, StepProgress } from "./StepChrome";
import { DONE, type Draft, EMPTY_DRAFT, type PayoutDraft } from "./draft";
import { BusinessStep } from "./steps/BusinessStep";
import { DoneView } from "./steps/DoneView";
import { PayoutStep } from "./steps/PayoutStep";
import { ReviewStep } from "./steps/ReviewStep";

export { DONE } from "./draft";

/**
 * Onboarding for a new profile: business name, live payout wallet, review. Everything is
 * collected first and sent in one request on "Create profile", which creates the profile and its
 * payout settings together. The profile becomes active only when the user leaves the final
 * screen, through `onFinish(profileId, to)`, which the host uses to switch to it and navigate.
 */
export function ProfileWizard({
  header,
  cancel,
  footer,
  onFinish,
}: {
  /** Top-right actions; gets the step (`DONE` once the profile exists). */
  header?: (step: number) => ReactNode;
  /** Secondary button on the first step. */
  cancel?: ReactNode;
  /** Shown on the first step, in the side panel (below the form on small screens). */
  footer?: ReactNode;
  onFinish: (profileId: string, to: string) => void;
}) {
  useDocumentTitle("Create a business profile");
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  // The furthest step opened so far; any step up to it can be revisited.
  const [reached, setReached] = useState(0);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [profile, setProfile] = useState<Profile>();
  const update = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  // Stable, so the payout form can depend on it in effects.
  const setPayout = useCallback(
    (patch: Partial<PayoutDraft>) => setDraft((d) => ({ ...d, payout: { ...d.payout, ...patch } })),
    [],
  );

  function finish(to: string) {
    if (!profile) return;
    rememberProfile(profile.id);
    queryClient.setQueryData<Me>(ME_KEY, (me) => me && { ...me, profiles: [...me.profiles, profile] });
    void queryClient.invalidateQueries({ queryKey: ME_KEY });
    onFinish(profile.id, to);
  }

  const locked = step === 0 && !draft.name.trim();
  const go = (next: number) => {
    setStep(next);
    setReached((r) => Math.max(r, next));
  };

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[22rem_1fr]">
      <aside className="sticky top-0 hidden h-dvh flex-col border-r border-line bg-surface px-6 py-8 lg:flex">
        <Logo className="self-start" />
        <div className="mt-12 px-3">
          <h2 className="text-base font-semibold">Set up your business</h2>
          <p className="mt-1 text-[0.8125rem] text-fg-muted">
            A few details, then you are ready to accept payments.
          </p>
        </div>
        <nav aria-label="Setup steps" className="mt-6">
          <StepNav step={step} reached={reached} locked={locked} onSelect={go} />
        </nav>
        {footer && step === 0 && <div className="mt-auto">{footer}</div>}
      </aside>
      <div className="flex min-h-dvh flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-line bg-surface px-4 sm:px-8 lg:border-0 lg:bg-transparent">
          <span className="lg:hidden">
            <Logo />
          </span>
          <span className="ml-auto">{header?.(step)}</span>
        </header>
        <main className="mx-auto w-full max-w-xl flex-1 px-4 pt-6 pb-16 sm:px-8 sm:pt-10 lg:pt-16">
          <div className="mb-8 lg:hidden">
            <StepProgress step={step} />
          </div>
          {step === 0 && <BusinessStep draft={draft} update={update} next={() => go(1)} cancel={cancel} />}
          {step === 1 && (
            <PayoutStep
              payout={draft.payout}
              setPayout={setPayout}
              next={() => go(2)}
              back={() => setStep(0)}
            />
          )}
          {step === 2 && (
            <ReviewStep
              draft={draft}
              goTo={setStep}
              onCreated={(p) => {
                setProfile(p);
                setStep(DONE);
              }}
            />
          )}
          {step === DONE && profile && <DoneView profile={profile} finish={finish} />}
          {footer && step === 0 && <div className="mt-10 lg:hidden">{footer}</div>}
        </main>
      </div>
    </div>
  );
}
